/**
 * RuntimeManagerProjectile.ts
 *
 * Manages projectile spawning, pooling, and collision groups.
 */

import Phaser from 'phaser';
import { Projectile, ProjectileEntityConfig } from './Projectile';
import { ProjectileConfig } from './ProjectileTypes';
import { DataManagerProjectile } from './DataManagerProjectile';
import { Allegiance } from '../weapons/DataManagerWeapon';
import { Entity } from '../entities/base/Entity';
import { CollisionSystem } from '../systems/CollisionSystem';

// Pool configuration
const POOL_SIZE = 20; // Initial pool size per projectile type
const MAX_POOL_SIZE = 100; // Max per type

export interface ProjectileSpawnEvent {
  type: string;
  x: number;
  y: number;
  angle: number;
  speed?: number;
  damage?: number;
  allegiance: Allegiance;
  shooterId?: number;
  targetId?: number;
  initialScaleX?: number;
  initialScaleY?: number;
  // Travel phase (for delayedLock with initial movement)
  travelTargetX?: number;
  travelTargetY?: number;
  travelSpeed?: number;
  travelStartScale?: number;
  travelEndScale?: number;
}

/**
 * RuntimeManagerProjectile - Handles all projectile spawning and lifecycle
 */
export class RuntimeManagerProjectile {
  private scene: Phaser.Scene;

  // Object pools per projectile type
  private pools: Map<string, Phaser.GameObjects.Group> = new Map();

  // Active projectiles for update loop
  private activeProjectiles: Set<Projectile> = new Set();

  // Collision groups
  private friendlyGroup: Phaser.GameObjects.Group;
  private enemyGroup: Phaser.GameObjects.Group;
  private destructibleEnemyGroup: Phaser.GameObjects.Group;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;

    // Create collision groups
    this.friendlyGroup = scene.add.group({
      classType: Projectile,
      runChildUpdate: false, // We handle updates manually
    });

    this.enemyGroup = scene.add.group({
      classType: Projectile,
      runChildUpdate: false,
    });

    this.destructibleEnemyGroup = scene.add.group({
      classType: Projectile,
      runChildUpdate: false,
    });

    // Listen for spawn events
    this.setupEventListeners();
  }

  /**
   * Setup event listeners
   */
  private setupEventListeners(): void {
    // Listen for weapon fire events
    this.scene.events.on('spawnProjectile', this.onSpawnProjectile, this);

    // Listen for projectile despawn
    this.scene.events.on('projectileDespawned', this.onProjectileDespawned, this);

    // Cleanup on scene shutdown
    this.scene.events.once('shutdown', this.destroy, this);
    this.scene.events.once('destroy', this.destroy, this);
  }

  /**
   * Handle spawn projectile event
   */
  private onSpawnProjectile(event: ProjectileSpawnEvent): void {
    // Resolve reference #projectiles.X → X
    let type = event.type;
    if (type?.startsWith('#projectiles.')) {
      type = type.substring('#projectiles.'.length);
    }

    this.spawn(type, event.x, event.y, {
      type: type,
      x: event.x,
      y: event.y,
      angle: event.angle,
      speed: event.speed,
      damage: event.damage,
      allegiance: event.allegiance,
      shooterId: event.shooterId,
      targetId: event.targetId,
      initialScaleX: event.initialScaleX,
      initialScaleY: event.initialScaleY,
      travelTargetX: event.travelTargetX,
      travelTargetY: event.travelTargetY,
      travelSpeed: event.travelSpeed,
      travelStartScale: event.travelStartScale,
      travelEndScale: event.travelEndScale,
    });
  }

  /**
   * Handle projectile despawned event
   */
  private onProjectileDespawned(data: { projectile: Projectile }): void {
    this.activeProjectiles.delete(data.projectile);
  }

  /**
   * Get or create pool for projectile type
   */
  private getPool(type: string): Phaser.GameObjects.Group {
    let pool = this.pools.get(type);

    if (!pool) {
      pool = this.scene.add.group({
        classType: Projectile,
        maxSize: MAX_POOL_SIZE,
        runChildUpdate: false,
      });
      this.pools.set(type, pool);
    }

    return pool;
  }

  /**
   * Spawn a projectile
   */
  spawn(type: string, x: number, y: number, config: ProjectileConfig): Projectile | undefined {
    const definition = DataManagerProjectile.get(type);
    if (!definition) {
      console.warn(`Unknown projectile type: ${type}`);
      return undefined;
    }

    // Apply spawn offset in the direction of fire
    let spawnX = x;
    let spawnY = y;
    if (definition.spawnOffset) {
      const angleRad = Phaser.Math.DegToRad(config.angle);
      spawnX += Math.cos(angleRad) * definition.spawnOffset;
      spawnY += Math.sin(angleRad) * definition.spawnOffset;
    }

    const pool = this.getPool(type);

    // Try to get from pool
    let projectile = pool.getFirstDead(false) as Projectile | null;

    if (projectile) {
      // Reuse pooled projectile
      projectile.reset({
        projectileType: type,
        x: spawnX,
        y: spawnY,
        angle: config.angle,
        speed: config.speed,
        damage: config.damage,
        allegiance: config.allegiance,
        shooterId: config.shooterId,
        targetId: config.targetId,
        initialScaleX: config.initialScaleX,
        initialScaleY: config.initialScaleY,
        travelTargetX: config.travelTargetX,
        travelTargetY: config.travelTargetY,
        travelSpeed: config.travelSpeed,
        travelStartScale: config.travelStartScale,
        travelEndScale: config.travelEndScale,
      });
    } else if (pool.getLength() < MAX_POOL_SIZE) {
      // Create new projectile
      projectile = new Projectile(this.scene, spawnX, spawnY, {
        projectileType: type,
        angle: config.angle,
        speed: config.speed,
        damage: config.damage,
        allegiance: config.allegiance,
        shooterId: config.shooterId,
        targetId: config.targetId,
        initialScaleX: config.initialScaleX,
        initialScaleY: config.initialScaleY,
        travelTargetX: config.travelTargetX,
        travelTargetY: config.travelTargetY,
        travelSpeed: config.travelSpeed,
        travelStartScale: config.travelStartScale,
        travelEndScale: config.travelEndScale,
      });

      pool.add(projectile);
    } else {
      // Pool is full, can't spawn
      console.warn(`Projectile pool full for type: ${type}`);
      return undefined;
    }

    // Activate the projectile
    projectile.activate();

    // Auto-targeting for homing projectiles without a target
    if (definition.behavior === 'homing' && !config.targetId) {
      const target = this.findNearestEnemyForProjectile(spawnX, spawnY, config.allegiance);
      if (target) {
        projectile.setTarget(target);
      }
    }

    // Add to active set
    this.activeProjectiles.add(projectile);

    // Add to appropriate collision group
    if (config.allegiance === Allegiance.Player) {
      this.friendlyGroup.add(projectile);
      this.enemyGroup.remove(projectile, false, false);
      this.destructibleEnemyGroup.remove(projectile, false, false);
    } else {
      this.enemyGroup.add(projectile);
      this.friendlyGroup.remove(projectile, false, false);

      // Add destructible enemy projectiles to special group
      if (definition.destructible) {
        this.destructibleEnemyGroup.add(projectile);
      }
    }

    // Emit spawned event (check scene exists)
    if (this.scene?.events) {
      this.scene.events.emit('projectileSpawned', { projectile });
    }

    return projectile;
  }

  /**
   * Despawn a projectile (return to pool)
   */
  despawn(projectile: Projectile): void {
    projectile.returnToPool();
    this.activeProjectiles.delete(projectile);
  }

  /**
   * Update all active projectiles
   */
  update(time: number, delta: number): void {
    for (const projectile of this.activeProjectiles) {
      if (projectile.active) {
        projectile.update(time, delta);
      }
    }
  }

  /**
   * Get friendly projectiles group (for collision detection)
   */
  getFriendlyGroup(): Phaser.GameObjects.Group {
    return this.friendlyGroup;
  }

  /**
   * Get enemy projectiles group (for collision detection)
   */
  getEnemyGroup(): Phaser.GameObjects.Group {
    return this.enemyGroup;
  }

  /**
   * Get destructible enemy projectiles group (for player to shoot)
   */
  getDestructibleEnemyGroup(): Phaser.GameObjects.Group {
    return this.destructibleEnemyGroup;
  }

  /**
   * Get all active projectiles
   */
  getActiveProjectiles(): Projectile[] {
    return Array.from(this.activeProjectiles);
  }

  /**
   * Get active projectiles by allegiance
   */
  getProjectilesByAllegiance(allegiance: Allegiance): Projectile[] {
    return Array.from(this.activeProjectiles).filter(
      (p) => p.allegiance === allegiance
    );
  }

  /**
   * Find homing target for a projectile
   */
  findHomingTarget(
    projectile: Projectile,
    targetAllegiance: Allegiance,
    maxRange: number = 500
  ): Entity | undefined {
    // This would need access to an entity registry
    // For now, emit an event and let the game scene handle it
    let target: Entity | undefined;

    // Check scene exists
    if (!this.scene?.events) return undefined;

    this.scene.events.emit('findHomingTarget', {
      projectile,
      targetAllegiance,
      maxRange,
      setTarget: (t: Entity) => {
        target = t;
      },
    });

    return target;
  }

  /**
   * Find nearest enemy for a projectile based on its allegiance
   * Uses CollisionSystem.findNearestEnemy with proper filtering
   *
   * @param x Source X position
   * @param y Source Y position
   * @param projectileAllegiance The allegiance of the projectile (Allegiance.Player or Allegiance.Enemy)
   * @returns The nearest valid target entity
   */
  private findNearestEnemyForProjectile(
    x: number,
    y: number,
    projectileAllegiance: Allegiance
  ): Entity | undefined {
    // Get CollisionSystem from scene registry
    const collisionSystem = this.scene.registry.get('collisionSystem') as CollisionSystem | undefined;
    if (!collisionSystem) {
      console.warn('[RuntimeManagerProjectile] CollisionSystem not found in registry');
      return undefined;
    }

    // For player projectiles (missiles): only target enemies, not neutrals
    // For enemy projectiles: would target the player (not handled here)
    if (projectileAllegiance === Allegiance.Player) {
      // onlyHostile = true: only entities with allegiance === Allegiance.Enemy
      return collisionSystem.findNearestEnemy(x, y, true);
    }

    return undefined;
  }

  /**
   * Clear all projectiles
   */
  clear(): void {
    // Despawn all active projectiles (only if not during shutdown)
    if (!this.isDestroyed) {
      for (const projectile of this.activeProjectiles) {
        projectile.returnToPool();
      }
    }
    this.activeProjectiles.clear();

    // Just clear group references - don't destroy groups
    // Check if group is still valid before clearing (may already be destroyed by Phaser)
    if (this.friendlyGroup?.children) this.friendlyGroup.clear(false, false);
    if (this.enemyGroup?.children) this.enemyGroup.clear(false, false);
    if (this.destructibleEnemyGroup?.children) this.destructibleEnemyGroup.clear(false, false);

    // Clear pools
    for (const pool of this.pools.values()) {
      if (pool?.children) pool.clear(false, false);
    }
    this.pools.clear();
  }

  /**
   * Get statistics for debugging
   */
  getStats(): {
    activeCount: number;
    pooledCount: number;
    friendlyCount: number;
    enemyCount: number;
  } {
    let pooledCount = 0;
    for (const pool of this.pools.values()) {
      pooledCount += pool.getLength();
    }

    return {
      activeCount: this.activeProjectiles.size,
      pooledCount,
      friendlyCount: this.friendlyGroup.getLength(),
      enemyCount: this.enemyGroup.getLength(),
    };
  }

  /**
   * Cleanup
   */
  destroy(): void {
    // Prevent double destroy
    if (this.isDestroyed) return;
    this.isDestroyed = true;

    // Remove event listeners (check if scene still exists)
    if (this.scene?.events) {
      this.scene.events.off('spawnProjectile', this.onSpawnProjectile, this);
      this.scene.events.off('projectileDespawned', this.onProjectileDespawned, this);
    }

    // Clear everything
    this.clear();
  }

  private isDestroyed = false;
}
